import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { Check } from "lucide-react";
import { Seal } from "@/components/brand/Seal";
import { Eyebrow } from "@/components/brand/Eyebrow";
import { BackLink } from "@/components/brand/BackLink";
import { getOrCreateSessionId } from "@/lib/academy-session";
import { loadConfirmation, scheduleDiscoveryCall } from "@/lib/apply.functions";

const searchSchema = z.object({ id: z.string().optional() });

export const Route = createFileRoute("/careers/apply/confirmation")({
  validateSearch: (s) => searchSchema.parse(s),
  head: () => ({
    meta: [
      { title: "Application Received — Currency Thoughts" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: ConfirmationPage,
});

function ConfirmationPage() {
  const { id } = Route.useSearch();
  const [firstName, setFirstName] = useState("there");
  const load = useServerFn(loadConfirmation);
  const schedule = useServerFn(scheduleDiscoveryCall);

  useEffect(() => {
    if (!id) return;
    load({ data: { token: id } }).then((res) => {
      if (res.ok) setFirstName(res.first_name);
    });
  }, [id, load]);

  useEffect(() => {
    const handler = (e: MessageEvent) => {
      const data = e.data;
      if (
        data &&
        typeof data === "object" &&
        "event" in data &&
        data.event === "calendly.event_scheduled"
      ) {
        const url =
          (data.payload?.event?.uri as string | undefined) ??
          (data.payload?.invitee?.uri as string | undefined) ??
          "";
        schedule({
          data: {
            session_id: getOrCreateSessionId(),
            scheduled_at: new Date().toISOString(),
            url,
          },
        }).catch(() => {});
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [schedule]);

  return (
    <main className="bg-cream-50 min-h-screen">
      <div className="container mx-auto px-6 pt-10">
        <BackLink to="/" label="Back to home" />
      </div>
      <section className="pt-12 pb-24">
        <div className="container mx-auto px-6 text-center max-w-[720px]">
          <div className="mx-auto w-20 h-20 rounded-full bg-bronze-600 flex items-center justify-center mb-8">
            <Check className="w-10 h-10 text-navy-900" strokeWidth={2.5} />
          </div>
          <Eyebrow align="center">Application received</Eyebrow>
          <h1 className="font-serif text-4xl md:text-[56px] text-navy-900 mt-5 leading-[1.1]">
            Application received.
          </h1>
          <p className="font-sans text-[17px] text-charcoal-500 mt-5 leading-[1.6]">
            Thank you, {firstName}. We're already reviewing your fit — personally, not
            algorithmically.
          </p>
        </div>
      </section>

      <section className="pb-16">
        <div className="container mx-auto px-6">
          <div
            className="bg-white max-w-[640px] mx-auto p-8"
            style={{
              borderRadius: "4px",
              borderTop: "3px solid var(--color-bronze-600, #A88B5C)",
              boxShadow:
                "0 10px 40px -10px rgba(168,139,92,0.18), 0 2px 8px rgba(168,139,92,0.08)",
            }}
          >
            <Eyebrow>What's next</Eyebrow>
            <ol className="mt-6 space-y-5">
              {[
                { k: "Today", v: "We review your application personally." },
                {
                  k: "Within 48 hours",
                  v: "We'll email you to schedule a 30-minute discovery call.",
                },
                { k: "If aligned", v: "1:1 with the founder." },
              ].map((s, i) => (
                <li key={s.k} className="flex items-start gap-4">
                  <span
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-bronze-600 font-serif text-[15px] text-bronze-600"
                    aria-hidden
                  >
                    {i + 1}
                  </span>
                  <div>
                    <p className="font-sans text-[11px] font-semibold uppercase tracking-[0.18em] text-bronze-600">
                      {s.k}
                    </p>
                    <p className="font-sans text-[15px] text-charcoal-900 mt-1">
                      {s.v}
                    </p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      <section className="pb-24">
        <div className="container mx-auto px-6 max-w-[820px]">
          <div className="text-center mb-6">
            <h2 className="font-serif text-2xl md:text-3xl text-navy-900">
              Want to skip the email back-and-forth?
            </h2>
            <p className="font-sans text-[14px] text-charcoal-500 mt-2">
              Schedule your discovery call directly below.
            </p>
          </div>
          <div
            className="bg-white overflow-hidden"
            style={{
              borderRadius: "4px",
              border: "1px solid rgba(168,139,92,0.3)",
            }}
          >
            <iframe
              title="Schedule discovery call"
              src="https://calendly.com/currencythoughts/discovery"
              className="w-full"
              style={{ height: 720, border: "none" }}
            />
          </div>
          <p className="text-center mt-6 font-sans text-[13px] text-charcoal-500">
            Or just wait — we'll reach out within 48 hours.{" "}
            <Link to="/" className="text-bronze-600 hover:underline">
              Back to home
            </Link>
          </p>
        </div>
      </section>
    </main>
  );
}